import React, { useState, useMemo, useEffect } from 'react'
import {
  Box,
  Tabs,
  Tab,
  Badge,
} from '@mui/material'
import { AdminPageFrame } from 'components/AdminPageFrame'
import { MobileTabSelect } from 'components/MobileTabSelect'
import { useSnackbar } from '@/hooks/common/useSnackbar'
import { TransporterWithPackages } from '@otw/models'
import { useQueryClient } from '@tanstack/react-query'
import { Timestamp } from 'firebase/firestore'
import useAdminTransporter from 'hooks/admin/useAdminTransporter'
import {
  useRepairUsers,
  upsertRepairUserDocument,
  deleteRepairUserDocument,
  normalizeRepairUserShopIds,
} from '@/hooks/admin/useRepairUsers'
import { useUsers } from 'hooks/useUsers'
import { useAuth } from 'contexts/AuthContext'
import { apiClient } from '@/core/api/apiClient'
import type { LogisticsUserGridRow } from './components/views/UsersGrid'
import { CreateTransporterDialog, CreateUserDialog, EditTransporterDialog, EditGroupDialog, PhotoTemplateConfigDialog } from './components/dialogs'
import { TransporterTab, GroupsTab, RepairV2Tab, InventoryTab, TransportTab, VehiclesTabV2, InspectionChecklistTab, MissingTransporterPhotosTab } from './components/tabs'
import { useTransporterDriverRequests } from '@/hooks/transporter/useTransporterDriverRequests'
import { useLocation, useNavigate } from 'react-router-dom'


const TransporterManagement = () => {
  const location = useLocation()
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const { showSuccess, showError, showInfo, showSnackbar, showWarning } = useSnackbar()
  const { userInfo } = useAuth()
  const [activeTab, setActiveTab] = useState(0)
  // Transporter state
  const [isCreateTransporterDialogOpen, setIsCreateTransporterDialogOpen] = useState(false)
  const [isCreateUserDialogOpen, setIsCreateUserDialogOpen] = useState(false)
  const [isEditTransporterDialogOpen, setIsEditTransporterDialogOpen] = useState(false)
  const [isEditGroupDialogOpen, setIsEditGroupDialogOpen] = useState(false)
  const [selectedTransporter, setSelectedTransporter] = useState<TransporterWithPackages | null>(null)
  const [selectedGroup, setSelectedGroup] = useState<any>(null)
  const [selectedUserToEdit, setSelectedUserToEdit] = useState<TransporterWithPackages | null>(null)
  const [processingDriverRequestId, setProcessingDriverRequestId] = useState<string | null>(null)

  // Template config state
  const [isTemplateConfigOpen, setIsTemplateConfigOpen] = useState(false)
  const [selectedCompanyId, setSelectedCompanyId] = useState<string | undefined>(undefined)

  const {
    transporters,
    transporterGroups,
    createTransporterGroup,
    updateTransporterGroup,
    deleteTransporterGroup,
    deleteLogisticsUserCompletely,
    deleteTransporterProfileOnly,
    updateTransporter,
    mergeCreateTransporterDocument,
  } = useAdminTransporter()
  const {
    pendingRequests: pendingDriverUserRequests,
    refetch: refetchDriverUserRequests,
  } = useTransporterDriverRequests()

  const { repairUsers, repairUsersById } = useRepairUsers()

  const { users } = useUsers()

  // Filter users to only include transporters
  const transporterUsers = users.filter(user => user.role === 'transporter')

  const usersForUsersTab = useMemo(() => {
    const parseDateToTimestamp = (value?: Timestamp | Date | string): Timestamp => {
      if (value && typeof value === 'object' && 'toDate' in value && typeof value.toDate === 'function') {
        return value as Timestamp
      }
      if (value instanceof Date) {
        return Timestamp.fromDate(value)
      }
      if (typeof value === 'string') {
        const parsedDate = new Date(value)
        const safeDate = Number.isNaN(parsedDate.getTime()) ? new Date() : parsedDate
        return Timestamp.fromDate(safeDate)
      }
      return Timestamp.fromDate(new Date())
    }

    const idSet = new Set<string>()
    transporters.forEach((t) => idSet.add(t.id))
    repairUsers.forEach((r) => idSet.add(r.id))

    const merged: LogisticsUserGridRow[] = [...idSet]
      .sort((a, b) => a.localeCompare(b))
      .map((userId) => {
        const t = transporters.find((x) => x.id === userId)
        const r = repairUsers.find((x) => x.id === userId)

        if (t && r) {
          const rShops = normalizeRepairUserShopIds(r)
          return {
            ...t,
            packages: [],
            transporter_role: t.transporter_role || 'driver',
            hasTransportAccess: true,
            hasRepairAccess: true,
            repair_shop_id: rShops[0],
            repair_shop_ids: rShops,
          }
        }
        if (t) {
          return {
            ...t,
            packages: [],
            transporter_role: t.transporter_role || 'driver',
            hasTransportAccess: true,
            hasRepairAccess: false,
          }
        }
        const repairUser = r!
        const fullName = (repairUser.name || '').trim()
        const [firstName, ...lastNameParts] = fullName.split(' ')
        return {
          id: repairUser.id,
          user_id: repairUser.id,
          email: repairUser.email || '',
          phone: repairUser.phone || '',
          first_name: firstName || '',
          last_name: lastNameParts.join(' ') || '',
          created_at: parseDateToTimestamp(repairUser.createdAt),
          updated_at: parseDateToTimestamp(repairUser.updatedAt),
          status: 'ACTIVE' as const,
          transporter_role: 'repair' as any,
          elevated: false,
          packages: [],
          hasTransportAccess: false,
          hasRepairAccess: true,
          repair_shop_id: normalizeRepairUserShopIds(repairUser)[0],
          repair_shop_ids: normalizeRepairUserShopIds(repairUser),
          ...(repairUser.shop_id ? { shop_id: repairUser.shop_id } : {}),
        }
      })

    return merged
  }, [transporters, repairUsers])

  const transporterById = useMemo(() => {
    return new Map(transporters.map((transporter) => [transporter.id, transporter]))
  }, [transporters])

  const clearTransportDeepLinkParams = React.useCallback(() => {
    const params = new URLSearchParams(location.search)
    const keysToClear = [
      'transportTruckId',
      'transportVin',
      'shipmentPlanId',
      'openLegPhotos',
      'openPhotos',
      'vehicleId',
    ]
    let didChange = false
    keysToClear.forEach((key) => {
      if (params.has(key)) {
        params.delete(key)
        didChange = true
      }
    })
    if (!didChange) return

    const nextSearch = params.toString()
    navigate(
      {
        pathname: location.pathname,
        search: nextSearch ? `?${nextSearch}` : '',
      },
      { replace: true }
    )
  }, [location.pathname, location.search, navigate])

  const handleTabChange = (_event: React.SyntheticEvent, newValue: number) => {
    clearTransportDeepLinkParams()
    setActiveTab(newValue)
  }

  useEffect(() => {
    const params = new URLSearchParams(location.search)
    if (params.get('transportTruckId') || params.get('shipmentPlanId')) {
      setActiveTab(0)
    }
  }, [location.search])

  const tabLabels = [
    'Transport',
    'Repair',
    'Trucks',
    'Needs transport',
    'Users',
    'Companies',
    'Inspection Checklist',
    'Missing photos',
  ]
  const handleCreateTransporter = async (transporterData: {
    name: string
    driver_ids: string[]
    admin_ids: string[]
    owner_id: string
    price_per_mile?: number
  }) => {
    try {
      // Create a transporter company with the selected transporters
      await createTransporterGroup({
        name: transporterData.name,
        owner_id: transporterData.owner_id,
        driver_ids: transporterData.driver_ids,
        admin_ids: transporterData.admin_ids,
        price_per_mile: transporterData.price_per_mile,
      })
      setIsCreateTransporterDialogOpen(false)
    } catch (error) {
      console.error('Error creating transporter company:', error)
      showError('Error creating transporter company')
    }
  }

  const handleCreateUserSuccess = async (userData: {
    id: string
    email: string
    firstName: string
    lastName: string
    phoneNumber: string
    transporterRole: string
    userType?: 'transporter' | 'repair' | 'both'
    elevated?: boolean
    driverLicenseFrontStoragePath?: string
    companyId?: string
    companyName?: string
    repairShopId?: string
    repairShopIds?: string[]
    repairShopName?: string
  }) => {
    // The API endpoint already creates the transporter document with ID = user_id
    // but we need to update it with the elevated field if set
    try {
      // Update transporter document with elevated field if needed
      // The transporter document ID is the same as the user ID
      if (userData.elevated !== undefined || userData.driverLicenseFrontStoragePath) {
        try {
          const transporterPatch: { elevated?: boolean; driver_license_front_storage_path?: string } = {}
          if (userData.elevated !== undefined) {
            transporterPatch.elevated = userData.elevated
          }
          if (userData.driverLicenseFrontStoragePath) {
            transporterPatch.driver_license_front_storage_path = userData.driverLicenseFrontStoragePath
          }
          await updateTransporter(userData.id, transporterPatch)
        } catch (transporterError) {
          console.error('Error updating transporter elevated field:', transporterError)
          showError('Error updating elevated privileges')
          // Don't fail the entire operation if elevated update fails
        }
      }

      // Invalidate queries to refresh the transporter list and groups
      queryClient.invalidateQueries({ queryKey: ['transporters'] })
      queryClient.invalidateQueries({ queryKey: ['transporters_with_packages'] })
      queryClient.invalidateQueries({ queryKey: ['transporter_groups'] })
      
      // Show success message with assignment info
      if (userData.userType === 'repair') {
        if (userData.repairShopName) {
          showSuccess(`Repair user created successfully and linked to ${userData.repairShopName}`)
        } else {
          showSuccess('Repair user created successfully')
        }
      } else if (userData.userType === 'both') {
        const details = [
          userData.companyName ? `company: ${userData.companyName}` : null,
          userData.repairShopName ? `repair shop: ${userData.repairShopName}` : null,
        ].filter(Boolean)
        showSuccess(
          details.length > 0
            ? `Dual-access user created successfully (${details.join(' • ')})`
            : 'Dual-access user created successfully'
        )
      } else if (userData.companyName) {
        showSuccess(`Transporter user created successfully and added to ${userData.companyName} as ${userData.transporterRole}`)
      } else {
        showSuccess('Transporter user created successfully')
      }
    } catch (error) {
      console.error('Error refreshing transporter data:', error)
      showError('Error refreshing transporter data')
    }
  }

  const handleEditUser = (transporter: TransporterWithPackages) => {
    setSelectedUserToEdit(transporter)
    setIsCreateUserDialogOpen(true)
  }

  const handleEditUserSuccess = async (userData: {
    id: string
    email: string
    firstName: string
    lastName: string
    phoneNumber: string
    transporterRole: string
    userType?: 'transporter' | 'repair' | 'both'
    elevated?: boolean
    driverLicenseFrontStoragePath?: string
    companyId?: string
    companyName?: string
    repairShopId?: string
    repairShopIds?: string[]
    repairShopName?: string
  }) => {
    try {
      if (selectedUserToEdit) {
        const selectedUserId = selectedUserToEdit.user?.id || selectedUserToEdit.user_id || selectedUserToEdit.id
        const shouldHaveRepairAccess = userData.userType === 'repair' || userData.userType === 'both'
        const shouldHaveTransportAccess = userData.userType === 'transporter' || userData.userType === 'both'

        if (!shouldHaveRepairAccess && repairUsersById.has(selectedUserId)) {
          try {
            await deleteRepairUserDocument(selectedUserId)
          } catch (repairDeleteError) {
            console.error('Error removing repair user document:', repairDeleteError)
            showError('Could not remove repair profile')
            throw repairDeleteError
          }
        }
        if (!shouldHaveTransportAccess && transporterById.has(selectedUserId)) {
          try {
            await deleteTransporterProfileOnly(selectedUserId)
          } catch (transportDeleteError) {
            console.error('Error removing transporter profile:', transportDeleteError)
            showError('Could not remove transporter profile')
            throw transportDeleteError
          }
        }

        if (shouldHaveRepairAccess) {
          try {
            const resolvedShopIds =
              userData.repairShopIds && userData.repairShopIds.length > 0
                ? userData.repairShopIds
                : userData.repairShopId
                  ? [userData.repairShopId]
                  : repairUsersById.get(selectedUserId)?.shop_ids ?? []
            await upsertRepairUserDocument({
              userId: selectedUserId,
              name: `${userData.firstName} ${userData.lastName}`,
              email: userData.email,
              phone: userData.phoneNumber,
              shopIds: resolvedShopIds,
            })
          } catch (repairError) {
            console.error('Error updating repair user document:', repairError)
            showError('Error updating repair user document')
            throw repairError
          }
        }
        if (shouldHaveTransportAccess) {
          try {
            const updateData: any = {
              email: userData.email,
              phone: userData.phoneNumber,
              first_name: userData.firstName,
              last_name: userData.lastName,
              transporter_role: userData.transporterRole as 'driver' | 'admin' | 'owner',
              status: 'ACTIVE',
              updated_at: Timestamp.now(),
            }
            
            // Always include elevated field, even if false
            if (userData.elevated !== undefined) {
              updateData.elevated = userData.elevated
            }
            if (userData.driverLicenseFrontStoragePath) {
              updateData.driver_license_front_storage_path = userData.driverLicenseFrontStoragePath
            }

            if (transporterById.has(selectedUserId)) {
              await updateTransporter(selectedUserId, updateData)
            } else {
              await mergeCreateTransporterDocument(selectedUserId, updateData)
            }
          } catch (transporterError) {
            console.error('Error updating transporter document:', transporterError)
            showError('Error updating transporter document')
            throw transporterError
          }
        }
      }

      // Invalidate queries to refresh the transporter list
      await queryClient.invalidateQueries({ queryKey: ['transporters'] })
      await queryClient.invalidateQueries({ queryKey: ['transporters_with_packages'] })
      await queryClient.invalidateQueries({ queryKey: ['transporter_groups'] })
      await queryClient.invalidateQueries({ queryKey: ['repair_users'] })
      await queryClient.invalidateQueries({ queryKey: ['users'] })
      
      showSuccess(
        userData.userType === 'repair'
          ? 'Repair user updated successfully'
          : userData.userType === 'both'
            ? 'Dual-access user updated successfully'
            : 'User updated successfully'
      )
      setSelectedUserToEdit(null)
    } catch (error) {
      console.error('Error refreshing transporter data:', error)
      showError('Error refreshing transporter data')
    }
  }

  const handleDeleteLogisticsUser = async (userId: string) => {
    try {
      await deleteLogisticsUserCompletely(userId)
    } catch (error) {
      console.error('Error deleting logistics user:', error)
      showError('Error deleting user')
    }
  }

  const handleApproveDriverUserRequest = async (requestId: string) => {
    try {
      setProcessingDriverRequestId(requestId)
      const result = await apiClient.portal.admin.approveTransporterDriverUserRequest(requestId)
      const createdUserId =
        (result as any)?.data?.createdUserId ?? (result as any)?.createdUserId
      await queryClient.invalidateQueries({ queryKey: ['transporters'] })
      await queryClient.invalidateQueries({ queryKey: ['transporters_with_packages'] })
      await queryClient.invalidateQueries({ queryKey: ['transporter_groups'] })
      await refetchDriverUserRequests()
      showSuccess(
        createdUserId
          ? `Driver created and assigned successfully (${createdUserId})`
          : 'Driver created and assigned successfully'
      )
    } catch (error) {
      console.error('Error approving driver user request:', error)
      showError(`Error approving request: ${error instanceof Error ? error.message : 'Unknown error'}`)
    } finally {
      setProcessingDriverRequestId(null)
    }
  }

  const handleRejectDriverUserRequest = async (requestId: string) => {
    try {
      setProcessingDriverRequestId(requestId)
      await apiClient.portal.admin.rejectTransporterDriverUserRequest(requestId)
      await refetchDriverUserRequests()
      showSuccess('Driver request rejected')
    } catch (error) {
      console.error('Error rejecting driver user request:', error)
      showError(`Error rejecting request: ${error instanceof Error ? error.message : 'Unknown error'}`)
    } finally {
      setProcessingDriverRequestId(null)
    }
  }

  const handleViewDriverUserRequestLicense = async (request: { driver_license_front_storage_path?: string }) => {
    try {
      if (!request.driver_license_front_storage_path) {
        showInfo('No license photo found for this request')
        return
      }
      const result = await apiClient.portal.admin.getTransporterLicenseFrontUrl(
        request.driver_license_front_storage_path
      )
      const url = (result as any)?.data?.url ?? (result as any)?.url
      if (!url) {
        showWarning('Unable to load license photo URL')
        return
      }
      window.open(url, '_blank', 'noopener,noreferrer')
    } catch (error) {
      console.error('Error opening driver license photo:', error)
      showError('Failed to open license photo')
    }
  }

  const handleViewTransporterLicense = async (transporter: { driver_license_front_storage_path?: string }) => {
    try {
      if (!transporter.driver_license_front_storage_path) {
        showInfo('No license photo found for this user')
        return
      }
      const result = await apiClient.portal.admin.getTransporterLicenseFrontUrl(
        transporter.driver_license_front_storage_path
      )
      const url = (result as any)?.data?.url ?? (result as any)?.url
      if (!url) {
        showWarning('Unable to load license photo URL')
        return
      }
      window.open(url, '_blank', 'noopener,noreferrer')
    } catch (error) {
      console.error('Error opening transporter license photo:', error)
      showError('Failed to open license photo')
    }
  }

  const handleEditTransporter = (transporter: TransporterWithPackages) => {
    setSelectedTransporter(transporter)
    setIsEditTransporterDialogOpen(true)
  }

  const handleUpdateTransporter = async (transporterData: {
    id: string
    email: string
    phone?: string
    first_name?: string
    last_name?: string
    user_ids: string[]
    transporterOwnerId?: string
  }) => {
    try {
      await updateTransporter(transporterData.id, {
        email: transporterData.email,
        phone: transporterData.phone,
        first_name: transporterData.first_name,
        last_name: transporterData.last_name,
        transporterOwnerId: transporterData.transporterOwnerId,
        // If we have multiple users, set the first user as the primary user_id for backward compatibility
        user_id:
          transporterData.user_ids && transporterData.user_ids.length > 0 ? transporterData.user_ids[0] : undefined,
      })
      showSuccess('Transporter updated successfully')
    } catch (error) {
      console.error('Error updating transporter:', error)
      showError('Error updating transporter')
      throw error
    }
  }

  const handleEditGroup = (group: any) => {
    setSelectedGroup(group)
    setIsEditGroupDialogOpen(true)
  }

  const handleUpdateGroup = async (groupId: string, driverIds: string[], adminIds: string[], pricePerMile?: number) => {
    try {
      await updateTransporterGroup({ 
        groupId, 
        driverIds,
        adminIds,
        pricePerMile
      })
      setIsEditGroupDialogOpen(false)
      setSelectedGroup(null)
    } catch (error) {
      console.error('Error updating group:', error)
      showError('Error updating group')
    }
  }

  const handleDeleteGroup = async (groupId: string) => {
    try {
      await deleteTransporterGroup(groupId)
    } catch (error) {
      console.error('Error deleting group:', error)
      showError('Error deleting group')
    }
  }



  // Check if user is admin or babyAdmin
  const isAdmin = userInfo?.role === 'admin'

  return (
    <AdminPageFrame
      title='Transporter Management'
      subtitle='Coordinate transport, repair logistics, and companies'
    >
    <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '100dvh' }}>

      <Box sx={{ p: 2 }}>
        <MobileTabSelect
          label='Section'
          ariaLabel='Transporter sections'
          value={activeTab}
          onChange={value => {
            clearTransportDeepLinkParams()
            setActiveTab(value)
          }}
          tabs={tabLabels.map((label, index) => ({
            value: index,
            label,
            badge: index === 4 && pendingDriverUserRequests.length > 0 ? pendingDriverUserRequests.length : undefined,
          }))}
          desktop={
            <Tabs
              value={activeTab}
              onChange={handleTabChange}
              indicatorColor='primary'
              textColor='primary'
              sx={{
                borderBottom: 1,
                borderColor: 'divider',
                '& .MuiTab-root': {
                  textTransform: 'none',
                  fontWeight: 500,
                },
              }}
              variant='scrollable'
              scrollButtons='auto'
            >
              {tabLabels.map((label, index) => (
                <Tab
                  key={index}
                  value={index}
                  label={
                    index === 4 && pendingDriverUserRequests.length > 0 ? (
                      <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.75 }}>
                        <Badge badgeContent={pendingDriverUserRequests.length} color='error'>
                          <span>{label}</span>
                        </Badge>
                      </Box>
                    ) : (
                      label
                    )
                  }
                />
              ))}
            </Tabs>
          }
        />
      </Box>

      <Box sx={{ flexGrow: 1, overflow: 'auto' }}>
        {activeTab === 0 ? (
          <TransportTab />
        ) : activeTab === 1 ? (
          <RepairV2Tab />
        ) : activeTab === 2 ? (
          <VehiclesTabV2 />
        ) : activeTab === 3 ? (
          <InventoryTab />
        ) : activeTab === 4 ? (
          <TransporterTab
            transportersWithPackages={usersForUsersTab}
            onEditTransporter={handleEditTransporter}
            onDeleteTransporter={handleDeleteLogisticsUser}
            onCreateTransporter={() => setIsCreateTransporterDialogOpen(true)}
            onCreateUser={() => {
              setSelectedUserToEdit(null)
              setIsCreateUserDialogOpen(true)
            }}
            onEditUser={handleEditUser}
            transporterGroups={transporterGroups}
            pendingDriverUserRequests={pendingDriverUserRequests}
            onApproveDriverUserRequest={handleApproveDriverUserRequest}
            onRejectDriverUserRequest={handleRejectDriverUserRequest}
            onViewDriverUserRequestLicense={handleViewDriverUserRequestLicense}
            onViewTransporterLicense={handleViewTransporterLicense}
            processingRequestId={processingDriverRequestId}
          />
        ) : activeTab === 5 ? (
          <GroupsTab 
            transporterGroups={transporterGroups}
            transporters={transporters}
            repairUsers={repairUsers}
            onCreateTransporter={handleCreateTransporter}
            onEditGroup={handleEditGroup}
            onDeleteGroup={handleDeleteGroup}
            onOpenTemplateConfig={isAdmin ? (companyId: string) => {
              setSelectedCompanyId(companyId)
              setIsTemplateConfigOpen(true)
            } : undefined}
          />
        ) : activeTab === 6 ? (
          <InspectionChecklistTab />
        ) : activeTab === 7 ? (
          <MissingTransporterPhotosTab />
        ) : null}
      </Box>

      {/* Create Transporter Dialog */}
      <CreateTransporterDialog
        open={isCreateTransporterDialogOpen}
        onClose={() => setIsCreateTransporterDialogOpen(false)}
        onSubmit={handleCreateTransporter}
        transporters={transporters}
      />

      {/* Create/Edit User Dialog */}
      <CreateUserDialog
        open={isCreateUserDialogOpen}
        onClose={() => {
          setIsCreateUserDialogOpen(false)
          setSelectedUserToEdit(null)
        }}
        onSuccess={selectedUserToEdit ? handleEditUserSuccess : handleCreateUserSuccess}
        userToEdit={selectedUserToEdit}
        repairUsersById={repairUsersById}
        transporterById={transporterById}
      />

      {/* Edit Transporter Dialog */}
      {selectedTransporter && (
        <EditTransporterDialog
          open={isEditTransporterDialogOpen}
          onClose={() => {
            setIsEditTransporterDialogOpen(false)
            setSelectedTransporter(null)
          }}
          onSave={handleUpdateTransporter}
          transporter={selectedTransporter}
          users={transporterUsers}
        />
      )}

      {/* Edit Group Dialog */}
      {selectedGroup && (
        <EditGroupDialog
          open={isEditGroupDialogOpen}
          onClose={() => {
            setIsEditGroupDialogOpen(false)
            setSelectedGroup(null)
          }}
          onSave={handleUpdateGroup}
          group={selectedGroup}
          transporters={transporters}
        />
      )}

      {/* Photo Template Config Dialog */}
      {isAdmin && (
        <PhotoTemplateConfigDialog
          open={isTemplateConfigOpen}
          onClose={() => {
            setIsTemplateConfigOpen(false)
            setSelectedCompanyId(undefined)
          }}
          companyId={selectedCompanyId}
        />
      )}
    </Box>
    </AdminPageFrame>
  )
}

export default TransporterManagement
